/**
 * PrivacyPolicyModal.tsx
 *
 * Standard React Privacy Policy modal using HTML/CSS
 * Replaces react-native-web version
 */
import React from 'react';
import { useTheme } from '../../services/ThemeContext';
import { ScrollableModal } from './Modal';

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({ isOpen, onClose }) => {
  const { currentTheme: colors } = useTheme();

  if (!isOpen) return null;

  return (
    <ScrollableModal isOpen={isOpen} onClose={onClose} title="Privacy Policy">
      <div style={{ color: colors.textMain }}>
        <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 16, marginBottom: 8, color: colors.primary }}>
          1. Information We Collect
        </div>
        <div style={{ fontSize: 14, lineHeight: '20px', marginBottom: 8 }}>
          <span style={{ fontWeight: 'bold' }}>Game Data:</span> We store your story progress, characters, and game state locally on your device. This data is used to provide the core functionality of the App.
        </div>
        <div style={{ fontSize: 14, lineHeight: '20px', marginBottom: 8 }}>
          <span style={{ fontWeight: 'bold' }}>API Keys:</span> If you provide your own AI service API keys, they are stored locally on your device and never transmitted to our servers. They are sent directly to the respective AI service providers.
        </div>
        <div style={{ fontSize: 14, lineHeight: '20px', marginBottom: 8 }}>
          <span style={{ fontWeight: 'bold' }}>Usage Data:</span> We may collect anonymous usage statistics to improve the App's performance and features.
        </div>

        <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 16, marginBottom: 8, color: colors.primary }}>
          2. How We Use Your Information
        </div>
        <div style={{ fontSize: 14, lineHeight: '20px', marginBottom: 8 }}>
          Your game data is used to:
        </div>
        <div style={{ fontSize: 14, lineHeight: '20px', marginBottom: 8 }}>
          • Save and load your game progress<br />
          • Generate AI-powered narrative content<br />
          • Provide personalized story experiences<br />
          • Improve App functionality and performance
        </div>

        <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 16, marginBottom: 8, color: colors.primary }}>
          3. Data Sharing and Disclosure
        </div>
        <div style={{ fontSize: 14, lineHeight: '20px', marginBottom: 8 }}>
          <span style={{ fontWeight: 'bold' }}>AI Service Providers:</span> Your story inputs are sent to third-party AI providers (such as Grok, Gemini, OpenAI, etc.) to generate narrative content. These providers have their own privacy policies governing data use.
        </div>
        <div style={{ fontSize: 14, lineHeight: '20px', marginBottom: 8 }}>
          <span style={{ fontWeight: 'bold' }}>No Sale of Personal Data:</span> We do not sell your personal data to third parties.
        </div>

        <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 16, marginBottom: 8, color: colors.primary }}>
          4. Data Storage and Security
        </div>
        <div style={{ fontSize: 14, lineHeight: '20px', marginBottom: 8 }}>
          Game data is stored locally on your device using platform-specific storage (AsyncStorage on mobile, localStorage on web). We implement reasonable security measures to protect your data, but no method of transmission or storage is completely secure.
        </div>

        <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 16, marginBottom: 8, color: colors.primary }}>
          5. Cookies and Tracking
        </div>
        <div style={{ fontSize: 14, lineHeight: '20px', marginBottom: 8 }}>
          The App may use cookies and similar technologies for analytics and advertising purposes. You can disable cookies in your device settings.
        </div>

        <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 16, marginBottom: 8, color: colors.primary }}>
          6. Advertising and Personalization
        </div>
        <div style={{ fontSize: 14, lineHeight: '20px', marginBottom: 8 }}>
          The App displays advertisements to support free access to features. Some ads may be personalized based on your usage. You can opt out of personalized ads through your device settings.
        </div>

        <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 16, marginBottom: 8, color: colors.primary }}>
          7. Your Rights
        </div>
        <div style={{ fontSize: 14, lineHeight: '20px', marginBottom: 8 }}>
          You have the right to access, correct, or delete your personal data. You can also export your game data at any time through the App's export functionality.
        </div>

        <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 16, marginBottom: 8, color: colors.primary }}>
          8. Children's Privacy
        </div>
        <div style={{ fontSize: 14, lineHeight: '20px', marginBottom: 8 }}>
          The App is not intended for children under 13. We do not knowingly collect personal information from children under 13.
        </div>

        <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 16, marginBottom: 8, color: colors.primary }}>
          9. Changes to This Policy
        </div>
        <div style={{ fontSize: 14, lineHeight: '20px', marginBottom: 8 }}>
          We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page.
        </div>

        <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 16, marginBottom: 8, color: colors.primary }}>
          10. Contact Us
        </div>
        <div style={{ fontSize: 14, lineHeight: '20px', marginBottom: 8 }}>
          If you have any questions about this privacy policy, please contact us through the App's support channels.
        </div>

        <div style={{ marginTop: 20, paddingTop: 10, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.1)', borderTopStyle: 'solid' }}>
          <div style={{ fontSize: 12, fontStyle: 'italic', color: colors.textMuted }}>
            Last Updated: December 26, 2025
          </div>
        </div>

        <button
          onClick={onClose}
          style={{
            width: '100%',
            padding: '14px 16px',
            marginTop: 24,
            borderRadius: 12,
            backgroundColor: colors.primary,
            border: 'none',
            color: colors.background,
            fontSize: 16,
            fontWeight: 'bold',
            cursor: 'pointer',
          }}
        >
          I Understand
        </button>
      </div>
    </ScrollableModal>
  );
};

export { PrivacyPolicyModal };
